<template>
  <div v-if="show" class="CartProgressBar">
    <div class="CartProgressBar__Tiers">
      <div v-for="tier in data" :key="tier" class="CartProgressBar__Tier">
        <Icon
          v-if="store.eligibleItemsSubtotal < tier.thresh"
          :icon="tier.badge"
        />
        <Icon v-else :icon="'check--goldenhour'" />
        <p
          class="p6"
          :class="{
            CartProgressBar__Complete:
              tier.thresh <= store.eligibleItemsSubtotal,
          }"
        >
          {{ tierTitle(tier) }}

          <Icon
            v-if="tier.thresh <= store.eligibleItemsSubtotal"
            :icon="'check--goldenhour'"
          />
        </p>
        <progress
          v-if="prevTiers.includes(tier) || currentTier === tier"
          aria-label="savings-bar"
          :max="tier.thresh"
          :value="store.eligibleItemsSubtotal"
          class="CartProgressBar__Progress"
        ></progress>
        <progress
          v-else
          :max="tier.thresh"
          :value="0"
          class="CartProgressBar__Progress"
        ></progress>
      </div>
    </div>
    <p class="p5 CartProgressBar__Text">{{ barText }}</p>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useCartStore } from './Composables/Store';
import Icon from '../Global/Icon.vue';

const props = defineProps({
  showFreeShippingCopy: {
    type: [String, Boolean],
    default: false,
  },
});

const store = useCartStore();

/**
 * Returns a function that appends an asterisk to a tier title if the tier type
 * is 'shipping' and showFreeShippingCopy is enabled.
 * @param {Object} tier - The tier object
 * @param {string} tier.type - The tier type
 * @param {string} tier.title - The tier title
 * @returns {string} The tier title, with an asterisk appended if applicable
 */
const tierTitle = computed(() => (tier) => {
  const showAsterisk =
    tier.type === 'shipping' && JSON.parse(props.showFreeShippingCopy);
  return showAsterisk ? `${tier.title}*` : tier.title;
});

const data = ref(false);
const show = ref(false);

const prevTiers = computed(() => {
  const tiers = [];
  if (data.value.length > 0) {
    data.value.forEach((tier) => {
      if (store.eligibleItemsSubtotal >= tier.thresh) {
        tiers.push(tier);
      }
    });
  }
  return tiers;
});

const currentTier = computed(() => {
  const unmetTiers = data.value.filter((tier) => {
    if (store.eligibleItemsSubtotal < tier.thresh) {
      return tier;
    }
  });
  return unmetTiers[0];
});

const amountToGo = computed(() => {
  return currentTier.value.thresh - store.eligibleItemsSubtotal;
});

const barText = computed(() => {
  if (currentTier.value) {
    const noun =
      currentTier.value?.type === 'gwp' || currentTier.value?.type === 'tote'
        ? `a ${currentTier.value.title}`
        : currentTier.value.title;

    return `You're just $${amountToGo.value.toFixed(2)} away from ${noun}!`;
  } else {
    const tiers = data.value;
    let message, nouns;

    switch (tiers.length) {
      case 1: {
        const noun =
          tiers[0].type === 'gwp' || tiers[0].type === 'tote'
            ? `a ${tiers[0].title}`
            : tiers[0].title;
        message = `You've received ${noun}!`;
        break;
      }
      case 2: {
        nouns = [];
        tiers.forEach((tier) => {
          if (tier.type === 'gwp' || tier.type === 'tote') {
            nouns.push(`a ${tier.title}`);
          } else {
            nouns.push(tier.title);
          }
        });
        message = `You've received ${nouns[0]} and ${nouns[1]}!`;
        break;
      }
      case 3: {
        nouns = [];
        tiers.forEach((tier) => {
          if (tier.type === 'gwp' || tier.type === 'tote') {
            nouns.push(`a ${tier.title}`);
          } else {
            nouns.push(tier.title);
          }
        });
        message = `You've received ${nouns[0]}, ${nouns[1]}, and ${nouns[2]}!`;
        break;
      }
    }

    return message;
  }
});

watch(
  () => store.subtotal,
  () => {
    if (data.value.length > 0) {
      data.value.forEach((tier) => {
        if (store.eligibleItemsSubtotal >= tier.thresh) {
          tier.positiveCallback();
        } else if (store.eligibleItemsSubtotal < tier.thresh) {
          tier.negativeCallback();
        }
      });
    }
  }
);

const setData = () => {
  const tiers = store.global?.progressBar;

  // Get the threshold for the tote & GWP from elsewhere in the promo object.
  // Prevents site ops from having to enter the same data twice in two places.
  const values = tiers.reduce((arr, tier) => {
    const tierDisabled = tier.title === '';
    if (tierDisabled) return arr;

    const value = {};
    const threshold = tier?.thresh ?? 0;

    value.type = tier.type;
    value.title = tier.title;
    value.badge = tier.badge;
    value.thresh = parseFloat((threshold / 100).toFixed(2));

    switch (tier.type) {
      case 'shipping':
        value.positiveCallback = () => {};
        value.negativeCallback = () => {};
        break;
      case 'tote':
        value.positiveCallback = store.addTote;
        value.negativeCallback = store.removeTote;
        break;
      case 'gwp':
        value.positiveCallback = store.displayGWP;
        value.negativeCallback = store.removeGWP;
        break;
    }

    arr.push(value);
    return arr;
  }, []);

  data.value = values;
  show.value = values.length > 0;
};

onMounted(() => {
  setData();
});
</script>
